import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Returns the count of pending appointments (proposed/counterProposed)
 * where the current user needs to take action.
 */
export const usePendingAppointmentsCount = () => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user) { setCount(0); return; }

    const fetchCount = async () => {
      const { data } = await supabase
        .from("appointments")
        .select("id, status, created_by, client_user_id, artisan_user_id")
        .or(`client_user_id.eq.${user.id},artisan_user_id.eq.${user.id}`)
        .in("status", ["proposed", "counterProposed"]);

      if (!data) { setCount(0); return; }

      // Count only appointments where the OTHER party proposed (user needs to act)
      const actionable = data.filter((apt) => {
        const isClient = apt.client_user_id === user.id;
        const isArtisan = apt.artisan_user_id === user.id;
        if (apt.status === "proposed") {
          return (isClient && apt.created_by === "artisan") || (isArtisan && apt.created_by === "client");
        }
        if (apt.status === "counterProposed") {
          return isClient || isArtisan; // both can act on counter
        }
        return false;
      });

      setCount(actionable.length);
    };

    fetchCount();

    const channel = supabase
      .channel("pending-appointments-count")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "appointments" },
        () => fetchCount()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  return count;
};
